import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, Upload, Building, CheckCircle, AlertTriangle, FileSpreadsheet, Trash2, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminSecurityStatus() {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [uploading, setUploading] = useState(false);
  const [viewingStatus, setViewingStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [localCompaniesRes, chartMogulRes, statusesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/companies`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/chartmogul/customers`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/nist-security/all`, { credentials: 'include' })
      ]);

      let allCompanies = [];
      
      // Add local companies
      if (localCompaniesRes.ok) {
        const localData = await localCompaniesRes.json();
        allCompanies = localData.map(c => ({
          ...c,
          source: 'local'
        }));
      }

      // Add ChartMogul customers
      if (chartMogulRes.ok) {
        const cmData = await chartMogulRes.json();
        const cmCompanies = (cmData.customers || []).map(c => ({
          company_id: c.chartmogul_uuid,
          name: c.name || c.company,
          source: 'chartmogul'
        }));
        allCompanies = [...allCompanies, ...cmCompanies];
      }

      // Sort by name
      allCompanies.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(allCompanies);

      if (statusesRes.ok) {
        const data = await statusesRes.json();
        setStatuses(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    toast.info('Processing NIST assessment...');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];

        const response = await fetch(`${BACKEND_URL}/api/nist-security/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            company_id: selectedCompany,
            file_name: file.name,
            file_data: base64Data
          })
        });

        if (response.ok) {
          const result = await response.json();
          toast.success(`Assessment uploaded! Score: ${result.overall_score}% (${result.grade})`);
          fetchData();
          setSelectedCompany('');
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Upload failed');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(`Failed to upload: ${error.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade.startsWith('B')) return 'text-accent';
    if (grade.startsWith('C')) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-primary';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.name || companyId;
  };

  // Get the identifier to use for the dropdown (company_id for local, chartmogul_uuid for ChartMogul)
  const getCompanyIdentifier = (company) => {
    return company.company_id;
  };

  const handleDelete = async (statusId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete the security assessment for "${companyName}"? You can upload a new assessment afterward.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/nist-security/${statusId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Security assessment deleted. You can now upload a new one.');
        fetchData();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete assessment');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Security Status (NIST)">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Security Status (NIST)">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-secondary" />
          <div>
            <h2 className="text-2xl font-mono font-bold">Security Status (NIST)</h2>
            <p className="text-sm text-muted-foreground">Upload and manage NIST framework assessments for customers</p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload NIST Assessment
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload an Excel spreadsheet (.xlsx) with NIST framework assessment data. 
            The file should contain sections for Identify, Protect, Detect & Respond, and Recover.
            <br />
            <span className="text-accent">Note: Uploading a new assessment will replace any existing one for that company.</span>
          </p>
          
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-sm">
              <label className="text-sm font-mono font-bold mb-2 block">Select Company</label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="font-mono">
                  <SelectValue placeholder="Choose a company..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {companies.map((company) => (
                    <SelectItem key={company.company_id} value={company.company_id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedCompany || uploading}
                className="bg-secondary text-secondary-foreground"
              >
                {uploading ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-r-transparent" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Upload Excel File
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Status List */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h3 className="font-mono font-bold">Customer Security Assessments</h3>
          </div>
          
          {statuses.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No assessments uploaded yet</p>
              <p className="text-sm text-muted-foreground mt-1">Upload your first NIST assessment above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Grade</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Risks</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Sections</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase">Last Updated</th>
                    <th className="px-6 py-3 text-right text-xs font-mono font-bold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {statuses.map((status) => (
                    <tr key={status.status_id} className="hover:bg-muted/5">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono">{getCompanyName(status.company_id)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-2xl font-mono font-bold ${getScoreColor(status.overall_score)}`}>
                          {status.overall_score}%
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xl font-mono font-bold ${getGradeColor(status.grade)}`}>
                          {status.grade}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-xs font-mono">
                          <span className="text-primary">{status.risk_summary?.mitigated || 0} mitigated</span>
                          <span className="text-muted-foreground">/</span>
                          <span>{status.risk_summary?.total || 0} total</span>
                        </div>
                        {status.risk_summary?.high > 0 && (
                          <span className="text-xs text-destructive">
                            {status.risk_summary.high} high risk
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {status.sections?.map((section, idx) => (
                            <span 
                              key={idx} 
                              className={`px-2 py-0.5 rounded text-xs font-mono ${
                                section.score >= 80 ? 'bg-primary/10 text-primary' :
                                section.score >= 60 ? 'bg-accent/10 text-accent' :
                                section.score >= 40 ? 'bg-yellow-500/10 text-yellow-500' :
                                'bg-destructive/10 text-destructive'
                              }`}
                              title={`${section.name}: ${section.score}%`}
                            >
                              {section.name.substring(0, 3)}: {section.score}%
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-muted-foreground font-mono">
                          {new Date(status.last_updated).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingStatus(status)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(status.status_id, getCompanyName(status.company_id))}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Delete Assessment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Details Modal */}
        {viewingStatus && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-card">
                <div>
                  <h3 className="text-xl font-mono font-bold">{getCompanyName(viewingStatus.company_id)}</h3>
                  <p className="text-sm text-muted-foreground">NIST Security Assessment Details</p>
                </div>
                <Button variant="ghost" onClick={() => setViewingStatus(null)}>
                  ✕
                </Button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Score Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Overall Score</p>
                    <p className={`text-3xl font-mono font-bold ${getScoreColor(viewingStatus.overall_score)}`}>
                      {viewingStatus.overall_score}%
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Grade</p>
                    <p className={`text-3xl font-mono font-bold ${getGradeColor(viewingStatus.grade)}`}>
                      {viewingStatus.grade}
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Mitigated</p>
                    <p className="text-3xl font-mono font-bold text-primary">
                      {viewingStatus.risk_summary?.mitigated || 0}
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">High Risk</p>
                    <p className="text-3xl font-mono font-bold text-destructive">
                      {viewingStatus.risk_summary?.high || 0}
                    </p>
                  </div>
                </div>

                {/* Section Details */}
                {viewingStatus.sections?.map((section, idx) => (
                  <div key={idx} className="bg-background border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-mono font-bold">{section.name}</h4>
                      <span className={`text-xl font-mono font-bold ${getScoreColor(section.score)}`}>
                        {section.score}%
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {section.mitigated_risks}/{section.total_risks} risks mitigated
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-muted/20 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            section.score >= 80 ? 'bg-primary' :
                            section.score >= 60 ? 'bg-accent' :
                            section.score >= 40 ? 'bg-yellow-500' :
                            'bg-destructive'
                          }`}
                          style={{ width: `${section.score}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminSecurityStatus;
